import { CalendarBlank, CalendarX } from '@phosphor-icons/react'
import { useNavigate } from 'react-router-dom'

import SearchableListButton from 'Organisms/SearchableListButton'
import CalendarEventItem from 'Pages/TimeTracker/Components/CalendarEventItem/CalendarEventItem'
import useSessionStore from 'Context/useSessionStore'
import { ROLE_ADMIN_NAME } from 'constant/roles'
import { SETTINGS_ROUTER } from 'constant/router'
import { TABS_OPTIONS_SETTINGS } from 'Pages/SettingPage/Components/TabsSettings/TabsSettings'

const CalendarDrawerEmptyState = ({ Icon, title, subtitle, action }) => (
  <div className="calendarEventsDrawer_empty">
    <div className="calendarEventsDrawer_emptyIcon">
      <Icon size={20} weight="bold" />
    </div>
    <h6 className="calendarEventsDrawer_emptyTitle">{title}</h6>
    <p className="calendarEventsDrawer_emptySubtitle">{subtitle}</p>
    {action}
  </div>
)

const CalendarEventsDrawer = ({
  open,
  setOpen,
  currentDayName,
  hasConnectedCalendars,
  calendarFilters,
  onChangeCalendarFilters,
  events,
  onIncludeEvent,
  onExcludeEvent
}) => {
  const { user } = useSessionStore((state) => ({ user: state.user }))
  const navigate = useNavigate()
  const isAdmin = user.role === ROLE_ADMIN_NAME

  const hide = () => setOpen(false)

  const handleGoToIntegrations = () => {
    hide()
    navigate(`${SETTINGS_ROUTER}?section=${TABS_OPTIONS_SETTINGS.INTEGRATIONS.key}`)
  }

  return (
    <aside
      className={`calendarEventsDrawer ${open ? 'calendarEventsDrawer--open' : ''}`}
      aria-hidden={!open}
      aria-label="Calendar events">
      <div className="calendarEventsDrawer_inner">
        <div className="calendarEventsDrawer_header">
          <h5 className="calendarEventsDrawer_title">Calendar events</h5>
          <button type="button" className="btn-close" aria-label="Close" onClick={hide} />
        </div>

        <div className="calendarEventsDrawer_body">
          {hasConnectedCalendars && (
            <p className="calendarEventsDrawer_subtitle">{currentDayName}</p>
          )}

          {hasConnectedCalendars && calendarFilters?.length > 0 && (
            <div className="calendarEventsDrawer_filter">
              <SearchableListButton
                id="filterByCalendarSource"
                name="Calendars"
                switches={calendarFilters}
                setSwitches={onChangeCalendarFilters}
                colorBtn="secondary"
              />
            </div>
          )}

          {!hasConnectedCalendars && (
            <CalendarDrawerEmptyState
              Icon={CalendarBlank}
              title="No calendar connected"
              subtitle={
                isAdmin
                  ? 'You have no calendars connected. Connect one and your events will show up here.'
                  : 'You have no calendars connected. Ask an admin to connect one in Settings → Integrations.'
              }
              action={
                isAdmin && (
                  <button type="button" className="btn btn-primary calendarEventsDrawer_emptyBtn" onClick={handleGoToIntegrations}>
                    Connect a calendar
                  </button>
                )
              }
            />
          )}

          {hasConnectedCalendars && events.length === 0 && (
            <CalendarDrawerEmptyState
              Icon={CalendarX}
              title="No events"
              subtitle="You have no calendar events for this day."
            />
          )}

          {hasConnectedCalendars && events.length > 0 && (
            <div className="calendarEventsDrawer_list">
              {events.map((event) => (
                <CalendarEventItem
                  key={event.id}
                  event={event}
                  color={calendarFilters?.find((calendar) => calendar.id === event.calendar_id)?.color}
                  onIncludeEvent={onIncludeEvent}
                  onExcludeEvent={onExcludeEvent}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </aside>
  )
}

export default CalendarEventsDrawer
